import React, { useState, useEffect, useMemo } from 'react';
import { Link, useParams, useNavigate } from 'react-router-dom';
import { DataTable } from 'primereact/datatable';
import { Column } from 'primereact/column';
import { TabView, TabPanel } from 'primereact/tabview';
import { makeRequest2 } from '../../../../../../utils/makeRequest';
import { toastify } from '../../../../../../utils/toast';
import Layout from '../../../../component/layout';
import { useFacilityStore } from '../../../../../../app/z-store/store';

const ACCOUNT_URL = '/api/app/power_meter_management/controllers/customer_management/get_customer_account';
const ACCOUNT_INVOICES_URL = '/api/app/power_meter_management/controllers/invoicing/invoices/account';

const STATUS_BADGES = {
    Paid: 'bg-success',
    'Partially Paid': 'bg-warning',
    Unpaid: 'bg-danger',
    Overdue: 'bg-danger',
    Pending: 'bg-info',
    Cancelled: 'bg-secondary',
};

const defaultCustomerData = {
    id: 'N/A',
    customerId: null,
    name: 'N/A',
    accountNumber: 'N/A',
    deviceId: 'N/A',
    status: 'N/A',
    phone: 'N/A',
    email: 'N/A',
    userType: 'Postpaid',
    meterId: 'N/A',
    unitName: 'N/A',
    updatedAt: 'N/A',
};

const formatNumber = (value) =>
    new Intl.NumberFormat('en-KE', { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(value || 0);

const formatDate = (value) => {
    if (!value) return 'N/A';
    const date = new Date(value);
    if (isNaN(date.getTime())) return 'N/A';
    return date.toISOString().slice(0, 10);
};

const formatDateTime = (value) => {
    if (!value) return 'N/A';
    const date = new Date(value);
    if (isNaN(date.getTime())) return 'N/A';
    const pad = (n) => String(n).padStart(2, '0');
    return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())} ${pad(date.getHours())}:${pad(date.getMinutes())}`;
};

// Power period invoices store yearMonth as "<start>_to_<end>".
const formatPeriod = (yearMonth) => (yearMonth || 'N/A').replace('_to_', ' to ');

const invoiceTotal = (invoice) => (invoice.powerCharges?.totalCharge || 0) + (invoice.balanceBroughtForward || 0);
const invoiceBalance = (invoice) => Math.max(0, invoiceTotal(invoice) - (invoice.amountPaid || 0));

function ViewPostpaidPowerCustomer() {
    const navigate = useNavigate();
    const { accountId } = useParams();
    const selectedFacilityId = useFacilityStore((state) => state.facilityId);

    const [customerData, setCustomerData] = useState(defaultCustomerData);
    const [invoices, setInvoices] = useState([]);
    const [loading, setLoading] = useState(false);
    const [invoicesLoading, setInvoicesLoading] = useState(false);
    const [activeTabIndex, setActiveTabIndex] = useState(0);

    useEffect(() => {
        if (accountId && selectedFacilityId) {
            fetchCustomerAccount(accountId);
        }
    }, [accountId, selectedFacilityId]);

    const fetchCustomerAccount = async (id) => {
        try {
            setLoading(true);
            const response = await makeRequest2(`${ACCOUNT_URL}/${id}`, 'GET');

            if (!response.success) {
                throw new Error(response.error || response.message || 'Error fetching customer data');
            }

            const account = response.data?.data || response.data;
            const details = {
                id: account._id || account.id,
                customerId: account.customerId,
                name: account.customerName || 'N/A',
                accountNumber: account.account_no || 'N/A',
                deviceId: account.deviceId || 'N/A',
                status: account.status || 'N/A',
                phone: account.phoneNumber || 'N/A',
                email: account.email || 'N/A',
                userType: account.payment_type || 'Postpaid',
                meterId: account.meterId || 'N/A',
                unitName: account.unitInfo?.unitName || 'N/A',
                updatedAt: formatDateTime(account.updatedAt),
            };
            setCustomerData(details);

            if (details.accountNumber !== 'N/A' && details.customerId) {
                fetchInvoices(details.accountNumber, details.customerId);
            }
        } catch (error) {
            console.error('Error fetching customer data:', error);
            toastify(error.message || 'Error loading customer information', 'error');
        } finally {
            setLoading(false);
        }
    };

    const fetchInvoices = async (accountNumber, customerId) => {
        try {
            setInvoicesLoading(true);
            const response = await makeRequest2(
                `${ACCOUNT_INVOICES_URL}/${encodeURIComponent(accountNumber)}/customer/${customerId}`,
                'GET'
            );

            if (response.success) {
                const payload = response.data?.data || response.data;
                const list = Array.isArray(payload) ? payload : payload?.data || payload?.invoices || [];
                setInvoices(list);

                // Water-style fallback: the account endpoint doesn't always carry the unit name.
                if (list.length > 0) {
                    setCustomerData((prev) =>
                        prev.unitName === 'N/A' ? { ...prev, unitName: list[0].unitName || 'N/A' } : prev
                    );
                }
            } else {
                setInvoices([]);
                toastify(response.error || response.message || 'Failed to fetch invoices', 'error');
            }
        } catch (error) {
            console.error('Error fetching invoices:', error);
            setInvoices([]);
            toastify('Error loading invoices', 'error');
        } finally {
            setInvoicesLoading(false);
        }
    };

    const summary = useMemo(() => {
        const active = invoices.filter((inv) => inv.status !== 'Cancelled');
        // Each invoice's balance brought forward already contains earlier arrears, so the
        // amount currently owed is the balance of the most recent invoice, not a sum.
        const latest = active.length > 0 ? active[0] : null;
        return {
            count: active.length,
            totalBilled: active.reduce((sum, inv) => sum + (inv.powerCharges?.totalCharge || 0), 0),
            totalPaid: active.reduce((sum, inv) => sum + (inv.amountPaid || 0), 0),
            outstanding: latest ? invoiceBalance(latest) : 0,
            unpaidCount: active.filter((inv) => ['Unpaid', 'Partially Paid', 'Overdue'].includes(inv.status)).length,
        };
    }, [invoices]);

    const handleView = (invoice) => {
        navigate('/facility/utility_management/invoice_management/power_invoice_page', {
            state: { invoiceId: invoice._id, invoiceData: invoice },
        });
    };

    const exportInvoicesCSV = () => {
        const header = [
            'Invoice Number', 'Period', 'Meter', 'Previous Reading', 'Current Reading', 'Usage (kWh)',
            'Charges (KES)', 'Balance B/F (KES)', 'Paid (KES)', 'Balance Due (KES)', 'Status', 'Issued', 'Due',
        ];
        const rows = invoices.map((inv) => [
            inv.invoiceNumber,
            formatPeriod(inv.yearMonth),
            inv.meterNumber || '',
            inv.meterReadings?.previousReading ?? 0,
            inv.meterReadings?.currentReading ?? 0,
            inv.meterReadings?.usage ?? 0,
            inv.powerCharges?.totalCharge ?? 0,
            inv.balanceBroughtForward ?? 0,
            inv.amountPaid ?? 0,
            invoiceBalance(inv),
            inv.status,
            formatDate(inv.dateIssued || inv.createdAt),
            formatDate(inv.dueDate),
        ]);

        const csv = [header, ...rows]
            .map((row) => row.map((cell) => `"${String(cell).replace(/"/g, '""')}"`).join(','))
            .join('\n');

        const url = URL.createObjectURL(new Blob([csv], { type: 'text/csv;charset=utf-8;' }));
        const link = document.createElement('a');
        link.href = url;
        link.download = `Power_Postpaid_Invoices_${customerData.accountNumber}.csv`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        URL.revokeObjectURL(url);
    };

    const statusBody = (row) => (
        <span className={`badge ${STATUS_BADGES[row.status] || 'bg-secondary'} text-light`}>{row.status}</span>
    );

    const InfoRow = ({ label, value }) => (
        <div className="list-group-item d-flex justify-content-between align-items-center px-0">
            <span className="text-muted">{label}</span>
            <span className="fw-bold">{value}</span>
        </div>
    );

    return (
        <Layout>
            <div className="page-header">
                <div className="page-block">
                    <div className="row align-items-center">
                        <div className="col-md-12">
                            <ul className="breadcrumb">
                                <li className="breadcrumb-item">
                                    <Link to="/facility/">Dashboard</Link>
                                </li>
                                <li className="breadcrumb-item">
                                    <Link to="/facility/utility_management/meter_management/power_meters">Power Meters</Link>
                                </li>
                                <li className="breadcrumb-item active">
                                    <span>View Meter</span>
                                </li>
                                <li className="breadcrumb-item active">
                                    <span>Postpaid Account</span>
                                </li>
                            </ul>
                        </div>
                    </div>
                </div>
            </div>

            <div className="row">
                <div className="col-sm-12">
                    <div className="card">
                        <div className="card-header">
                            <button className="btn btn-link p-0 border-0" onClick={() => navigate(-1)}>
                                <i className="ti ti-arrow-narrow-left"></i> Back
                            </button>
                        </div>
                    </div>

                    <div className="card">
                        <div className="card-header">
                            <h3>Power Customer Account Details</h3>
                        </div>

                        {loading && customerData.id === 'N/A' ? (
                            <div className="card-body">
                                <div className="d-flex justify-content-center">
                                    <div className="spinner-border text-primary" role="status">
                                        <span className="visually-hidden">Loading...</span>
                                    </div>
                                </div>
                            </div>
                        ) : (
                            <div className="card-body p-0">
                                <div className="row g-0">
                                    {/* Customer info - left */}
                                    <div className="col-md-3 border-end">
                                        <div className="p-3">
                                            <div className="d-flex align-items-center mb-4">
                                                <div className="avatar avatar-xl bg-light-warning rounded-circle me-3">
                                                    <i className="ti ti-bolt f-22"></i>
                                                </div>
                                                <div>
                                                    <h4 className="mb-1">{customerData.name}</h4>
                                                    <span
                                                        className={`badge ${customerData.status === 'Active' ? 'bg-success' : 'bg-danger'} text-light`}
                                                    >
                                                        {customerData.status}
                                                    </span>
                                                </div>
                                            </div>

                                            <div className="list-group list-group-flush">
                                                <InfoRow label="Account Type" value={customerData.userType} />
                                                <InfoRow label="Account Number" value={customerData.accountNumber} />
                                                <InfoRow label="Meter" value={customerData.meterId} />
                                                <InfoRow label="Device ID" value={customerData.deviceId} />
                                                <InfoRow label="Phone Number" value={customerData.phone} />
                                                <InfoRow label="Email" value={customerData.email} />
                                                <InfoRow label="Unit Name" value={customerData.unitName} />
                                                <InfoRow label="Last Updated" value={customerData.updatedAt} />
                                            </div>

                                            <div className="mt-3 p-3 bg-light-warning rounded">
                                                <h6 className="mb-2">Outstanding Balance</h6>
                                                {invoicesLoading ? (
                                                    <div className="d-flex justify-content-center">
                                                        <div className="spinner-border spinner-border-sm text-warning" role="status">
                                                            <span className="visually-hidden">Loading balance...</span>
                                                        </div>
                                                    </div>
                                                ) : (
                                                    <h3 className={`mb-0 ${summary.outstanding > 0 ? 'text-danger' : 'text-success'}`}>
                                                        KES {formatNumber(summary.outstanding)}
                                                    </h3>
                                                )}
                                                <small className="text-muted mt-1 d-block">Balance due on the latest invoice</small>
                                            </div>
                                        </div>
                                    </div>

                                    {/* Invoices - right */}
                                    <div className="col-md-9">
                                        <div className="row g-3 p-3 pb-0">
                                            <div className="col-md-3">
                                                <div className="border rounded p-3 h-100">
                                                    <small className="text-muted">Invoices</small>
                                                    <h4 className="mb-0">{summary.count}</h4>
                                                </div>
                                            </div>
                                            <div className="col-md-3">
                                                <div className="border rounded p-3 h-100">
                                                    <small className="text-muted">Total Billed</small>
                                                    <h4 className="mb-0">KES {formatNumber(summary.totalBilled)}</h4>
                                                </div>
                                            </div>
                                            <div className="col-md-3">
                                                <div className="border rounded p-3 h-100">
                                                    <small className="text-muted">Total Paid</small>
                                                    <h4 className="mb-0">KES {formatNumber(summary.totalPaid)}</h4>
                                                </div>
                                            </div>
                                            <div className="col-md-3">
                                                <div className="border rounded p-3 h-100">
                                                    <small className="text-muted">Unpaid Invoices</small>
                                                    <h4 className="mb-0">{summary.unpaidCount}</h4>
                                                </div>
                                            </div>
                                        </div>

                                        <TabView activeIndex={activeTabIndex} onTabChange={(e) => setActiveTabIndex(e.index)} className="border-0">
                                            <TabPanel header="Invoices">
                                                <div className="p-3">
                                                    <div className="d-flex justify-content-end mb-3">
                                                        <button
                                                            className="btn btn-outline-primary"
                                                            onClick={exportInvoicesCSV}
                                                            disabled={invoices.length === 0}
                                                        >
                                                            Export
                                                        </button>
                                                    </div>
                                                    <div className="dt-responsive table-responsive">
                                                        <DataTable
                                                            value={invoices}
                                                            emptyMessage="No invoices found for this account."
                                                            sortMode="multiple"
                                                            paginator
                                                            rows={10}
                                                            loading={invoicesLoading}
                                                            stripedRows
                                                            tableStyle={{ minWidth: '60rem' }}
                                                        >
                                                            <Column field="invoiceNumber" header="Invoice" sortable />
                                                            <Column
                                                                field="yearMonth"
                                                                header="Period"
                                                                body={(row) => formatPeriod(row.yearMonth)}
                                                                sortable
                                                            />
                                                            <Column
                                                                header="Readings"
                                                                body={(row) =>
                                                                    `${row.meterReadings?.previousReading ?? 0} → ${row.meterReadings?.currentReading ?? 0}`
                                                                }
                                                            />
                                                            <Column
                                                                header="Usage"
                                                                body={(row) => `${formatNumber(row.meterReadings?.usage)} kWh`}
                                                            />
                                                            <Column
                                                                header="Charges"
                                                                body={(row) => `KES ${formatNumber(row.powerCharges?.totalCharge)}`}
                                                            />
                                                            <Column
                                                                header="Balance B/F"
                                                                body={(row) => `KES ${formatNumber(row.balanceBroughtForward)}`}
                                                            />
                                                            <Column
                                                                header="Paid"
                                                                body={(row) => `KES ${formatNumber(row.amountPaid)}`}
                                                            />
                                                            <Column
                                                                header="Balance Due"
                                                                body={(row) => `KES ${formatNumber(invoiceBalance(row))}`}
                                                            />
                                                            <Column field="status" header="Status" body={statusBody} sortable />
                                                            <Column
                                                                field="dueDate"
                                                                header="Due"
                                                                body={(row) => formatDate(row.dueDate)}
                                                                sortable
                                                            />
                                                            <Column
                                                                header="Action"
                                                                body={(row) => (
                                                                    <button className="btn btn-sm btn-outline-primary" onClick={() => handleView(row)}>
                                                                        View
                                                                    </button>
                                                                )}
                                                            />
                                                        </DataTable>
                                                    </div>
                                                </div>
                                            </TabPanel>
                                        </TabView>
                                    </div>
                                </div>
                            </div>
                        )}
                    </div>
                </div>
            </div>
        </Layout>
    );
}

export default ViewPostpaidPowerCustomer;
